import { ReactNode, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscriptionStatus } from '@/hooks/useSubscriptionStatus';
import { SubscriptionBlockingOverlay } from '@/components/subscription/SubscriptionBlockingOverlay';
import { SubscriptionOverdueBanner } from '@/components/subscription/SubscriptionOverdueBanner';
import { AppSidebar } from './AppSidebar';
import {
  Loader2,
  Menu,
  HelpCircle,
  Sparkles,
  BarChart3,
  ShoppingBag,
  ShoppingCart,
  Users,
  Radio,
  Zap,
  Store,
  MessageSquare,
  Bot,
  Settings,
  Slash,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface AppLayoutProps {
  children: ReactNode;
}

interface BreadcrumbItem {
  label: string;
  icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
}

const BREADCRUMB_MAP: Record<string, BreadcrumbItem[]> = {
  '/': [
    { label: 'Dashboard', icon: BarChart3 },
  ],
  '/produtos': [
    { label: 'Sua Loja', icon: Store },
    { label: 'Produtos', icon: ShoppingBag },
  ],
  '/vendas': [
    { label: 'Sua Loja', icon: Store },
    { label: 'Vendas', icon: ShoppingCart },
  ],
  '/clientes': [
    { label: 'Sua Loja', icon: Store },
    { label: 'Clientes', icon: Users },
  ],
  '/lives': [
    { label: 'Motor de Vendas', icon: Zap },
    { label: 'Live Shop', icon: Radio },
  ],
  '/lives/executar': [
    { label: 'Motor de Vendas', icon: Zap },
    { label: 'Live Shop', icon: Radio },
  ],
  '/automacoes': [
    { label: 'Motor de Vendas', icon: Zap },
    { label: 'Automações', icon: Bot },
  ],
  '/mensagens': [
    { label: 'Motor de Vendas', icon: Zap },
    { label: 'Mensagens', icon: MessageSquare },
  ],
  '/configuracoes': [
    { label: 'Configurações', icon: Settings },
    { label: 'Minha Loja', icon: Store },
  ],
  '/configuracoes/envios': [
    { label: 'Configurações', icon: Settings },
    { label: 'Envios', icon: ShoppingBag },
  ],
  '/configuracoes/checkout': [
    { label: 'Configurações', icon: Settings },
    { label: 'Checkout', icon: ShoppingCart },
  ],
  '/configuracoes/integracoes': [
    { label: 'Configurações', icon: Settings },
    { label: 'Integrações', icon: Zap },
  ],
  '/configuracoes/assinatura': [
    { label: 'Configurações', icon: Settings },
    { label: 'Assinatura', icon: Sparkles },
  ],
  '/ajuda': [
    { label: 'Suporte', icon: HelpCircle },
  ],
  '/ajuda/tutoriais': [
    { label: 'Suporte', icon: HelpCircle },
    { label: 'Tutoriais', icon: HelpCircle },
  ],
};

function getBreadcrumbs(pathname: string): BreadcrumbItem[] {
  if (BREADCRUMB_MAP[pathname]) return BREADCRUMB_MAP[pathname];
  const match = Object.keys(BREADCRUMB_MAP)
    .filter((key) => key !== '/' && pathname.startsWith(key))
    .sort((a, b) => b.length - a.length)[0];
  return match ? BREADCRUMB_MAP[match] : [{ label: 'Dashboard', icon: BarChart3 }];
}

export function AppLayout({ children }: AppLayoutProps) {
  const { user, loading } = useAuth();
  const { hasActiveSubscription, reason, isLoading: subscriptionLoading, isOverdue, expiresAt } = useSubscriptionStatus();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/auth');
    }
  }, [user, loading, navigate]);

  if (loading || subscriptionLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  if (!hasActiveSubscription) {
    return <SubscriptionBlockingOverlay reason={reason} />;
  }

  const breadcrumbs = getBreadcrumbs(location.pathname);

  return (
    <div className="flex min-h-screen w-full overflow-x-hidden" style={{ backgroundColor: 'hsl(236 100% 97%)' }}>
      <AppSidebar
        collapsed={collapsed}
        onToggleCollapse={() => setCollapsed((v) => !v)}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />
      <main
        className={cn(
          'flex-1 flex flex-col h-screen overflow-hidden transition-all duration-300 ease-in-out',
          'ml-0 md:ml-[252px]',
          collapsed && 'md:ml-[68px]'
        )}
      >
        {isOverdue && expiresAt && (
          <SubscriptionOverdueBanner expiresAt={expiresAt} />
        )}
        <header className="shrink-0 flex h-14 md:h-20 items-center justify-between px-3 md:px-4 lg:px-8" style={{ backgroundColor: 'hsl(236 100% 97%)' }}>
          <div className="flex items-center gap-2 md:gap-3 min-w-0">
            <button
              onClick={() => setMobileOpen(true)}
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:text-foreground md:hidden"
            >
              <Menu className="h-5 w-5" />
            </button>
            <nav className="flex items-center gap-2 md:gap-3 min-w-0">
              {breadcrumbs.map((crumb, i) => {
                const Icon = crumb.icon;
                const isLast = i === breadcrumbs.length - 1;
                const isParent = i < breadcrumbs.length - 1;
                return (
                  <div key={crumb.label} className={cn(
                    "flex items-center gap-2 md:gap-3",
                    isParent && "hidden sm:flex"
                  )}>
                    {i > 0 && (
                      <span className="text-base md:text-lg text-foreground/20 font-light select-none">/</span>
                    )}
                    <div className="flex items-center gap-1.5 md:gap-2">
                      <Icon
                        className={cn(
                          'h-4 w-4 md:h-5 md:w-5 shrink-0',
                          isLast
                            ? 'text-foreground/50'
                            : 'text-foreground/30'
                        )}
                        strokeWidth={1.5}
                      />
                      <span
                        className={cn(
                          'truncate',
                          isLast
                            ? 'text-base md:text-lg font-medium text-foreground'
                            : 'text-base md:text-lg font-normal text-foreground/35'
                        )}
                      >
                        {crumb.label}
                      </span>
                    </div>
                  </div>
                );
              })}
            </nav>
          </div>
          <div className="flex items-center gap-1.5 md:gap-2 shrink-0">
            <button
              onClick={() => navigate('/ajuda')}
              className="flex h-8 w-8 md:h-9 md:w-9 items-center justify-center rounded-full text-[#574BFB]/70 transition-colors hover:bg-[#EAEFFE] hover:text-[#574BFB]"
            >
              <HelpCircle className="h-5 w-5" strokeWidth={1.6} />
            </button>
            <button className="flex h-8 w-8 md:h-9 md:w-9 items-center justify-center rounded-full bg-gradient-to-br from-[#988EFF] to-[#574BFB] text-white shadow-[0_2px_8px_rgba(152,142,255,0.35)]">
              <Sparkles className="h-4 w-4 md:h-[18px] md:w-[18px]" strokeWidth={2} />
            </button>
          </div>
        </header>
        <div className={cn(
          "flex-1 min-h-0 mx-2 md:mx-4 lg:mx-6 mb-2 md:mb-4 lg:mb-6 bg-card rounded-xl md:rounded-2xl lg:rounded-3xl shadow-[0_1px_3px_rgba(0,0,0,0.04)] overflow-x-hidden",
          location.pathname === "/lives/executar" ? "overflow-hidden" : "overflow-y-auto"
        )}>
          <div className={cn(
            "animate-fade-up",
            location.pathname === "/lives/executar" ? "p-3 md:p-4 lg:p-4 h-full" : "p-3 md:p-6 lg:p-8"
          )}>
            {children}
          </div>
        </div>
      </main>
    </div>
  );
}
